import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import Navigation from "@/components/Navigation";
import BadgeShareDialog from "@/components/BadgeShareDialog";
import { useLocale } from "@/i18n/LocaleContext";
import { Trophy, Gem, Star, ChevronRight, BadgeCheck, Share2 } from "lucide-react";

const avatarOf = (u) =>
  u.photos?.[0] || u.picture ||
  `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || "?")}&background=7c3aed&color=fff&size=200`;

const RANK_STYLES = [
  "bg-amber-400/20 text-amber-300 border-amber-400/40",
  "bg-slate-300/15 text-slate-300 border-slate-300/40",
  "bg-orange-700/25 text-orange-400 border-orange-500/40",
];

function MemberRow({ u, rank, isMe, onShare }) {
  const navigate = useNavigate();
  const { t } = useLocale();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/profile/${u.user_id}`)}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/profile/${u.user_id}`)}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-colors text-left cursor-pointer ${
        isMe ? "bg-amber-500/[0.06] border-amber-400/30" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.07]"
      }`}
      data-testid={`leaderboard-member-${u.user_id}`}
    >
      <span
        className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center text-sm font-bold ${
          RANK_STYLES[rank - 1] || "bg-white/5 text-muted-foreground border-white/10"
        }`}
      >
        {rank}
      </span>
      <img src={avatarOf(u)} alt={u.name} className="w-12 h-12 rounded-full object-cover shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold truncate">
            {u.name}
            {u.age ? `, ${u.age}` : ""}
          </span>
          {isMe && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300">{t("leaderboard.you")}</span>}
          {u.is_verified && <BadgeCheck className="w-4 h-4 text-primary shrink-0" />}
          {u.diamond_badge && <Gem className="w-4 h-4 text-cyan-400 shrink-0" />}
          {u.fire_badge && <span className="text-sm">🔥</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
          {u.review_count > 0 && (
            <span className="flex items-center gap-1 text-amber-400">
              <Star className="w-3 h-3 fill-amber-400" /> {Number(u.avg_rating).toFixed(1)} ({u.review_count})
            </span>
          )}
          <span>{u.completed_dates} {t("leaderboard.datesCompleted")}</span>
        </div>
      </div>
      {isMe ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShare();
          }}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-400/40 text-amber-300 hover:bg-amber-500/25 transition-colors"
          data-testid={`share-badge-btn-${u.user_id}`}
        >
          <Share2 className="w-3.5 h-3.5" /> {t("leaderboard.shareBadge")}
        </button>
      ) : (
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      )}
    </div>
  );
}

function Section({ title, subtitle, emblem, members, testid, sectionKey, myId, onShare }) {
  if (!members?.length) return null;
  return (
    <div className="mb-10" data-testid={testid}>
      <div className="flex items-center gap-3 mb-1">
        <span className="text-2xl">{emblem}</span>
        <h2 className="text-lg font-bold font-['Outfit']">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>
      <div className="space-y-3">
        {members.map((u, i) => (
          <MemberRow
            key={`${testid}-${u.user_id}`}
            u={u}
            rank={i + 1}
            isMe={u.user_id === myId}
            onShare={() => onShare({ member: u, section: sectionKey })}
          />
        ))}
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);

  useEffect(() => {
    api
      .get("/leaderboard")
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const empty = data && !data.fire?.length && !data.diamond?.length && !data.top_rated?.length;
  const ranked = Object.entries(data?.my_rank || {}).filter(([, v]) => v);
  const sectionTitles = {
    fire: t("leaderboard.fireTitle"),
    diamond: t("leaderboard.diamondTitle"),
    top_rated: t("leaderboard.ratedTitle"),
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="max-w-2xl mx-auto px-6 pt-24 pb-28" data-testid="leaderboard-page">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-8 h-8 text-amber-400" />
          <h1 className="text-4xl font-bold font-['Outfit'] tracking-tight">{t("leaderboard.title")}</h1>
        </div>
        <p className="text-muted-foreground mb-3">
          {t("leaderboard.subtitle")}
        </p>
        <p className="text-xs text-amber-400/90 mb-10 flex items-center gap-1.5" data-testid="champion-prize-note">
          <Trophy className="w-3.5 h-3.5" /> {t("leaderboard.prizeNote")}
        </p>

        {ranked.length > 0 && (
          <div className="mb-10 p-4 rounded-2xl bg-amber-500/10 border border-amber-400/40 flex items-center gap-3" data-testid="my-rank-banner">
            <Trophy className="w-7 h-7 text-amber-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-300">{t("leaderboard.youOnBoard")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {ranked.map(([k, v]) => `#${v} · ${sectionTitles[k]}`).join("  •  ")} — {t("leaderboard.youOnBoardSub")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShare({ member: user, section: ranked[0][0] })}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-400/40 text-amber-300 hover:bg-amber-500/25 transition-colors"
              data-testid="my-rank-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" /> {t("leaderboard.shareBadge")}
            </button>
          </div>
        )}

        {loading && <p className="text-muted-foreground">{t("leaderboard.loading")}</p>}

        {empty && (
          <div className="text-center py-16 text-muted-foreground" data-testid="leaderboard-empty">
            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>{t("leaderboard.empty")}</p>
          </div>
        )}

        {data && (
          <>
            <Section
              emblem="🔥"
              title={t("leaderboard.fireTitle")}
              subtitle={t("leaderboard.fireSub")}
              members={data.fire}
              testid="leaderboard-fire"
              sectionKey="fire"
              myId={user?.user_id}
              onShare={setShare}
            />
            <Section
              emblem="💎"
              title={t("leaderboard.diamondTitle")}
              subtitle={t("leaderboard.diamondSub")}
              members={data.diamond}
              testid="leaderboard-diamond"
              sectionKey="diamond"
              myId={user?.user_id}
              onShare={setShare}
            />
            <Section
              emblem="⭐"
              title={t("leaderboard.ratedTitle")}
              subtitle={t("leaderboard.ratedSub")}
              members={data.top_rated}
              testid="leaderboard-top-rated"
              sectionKey="top_rated"
              myId={user?.user_id}
              onShare={setShare}
            />
          </>
        )}

        <BadgeShareDialog
          open={!!share}
          onOpenChange={(o) => !o && setShare(null)}
          member={share?.member}
          section={share?.section}
        />
      </main>
    </div>
  );
}
